<template>
  <div class="metrics-chart-card" data-testid="metrics-average-score-card">
    <div class="metrics-chart-card__header">
      <span class="metrics-chart-card__title heading-s">{{
        $t("metrics.averageScore")
      }}</span>
      <eg-icon
        icon="information"
        class="metrics-chart-card__info-icon"
        data-testid="metrics-average-score-hint"
        @click.native="
          $eventBus.$emit('onboarding:clicked:metricsAverageScoreHint')
        "
      />
    </div>

    <div class="metrics-chart-card__body">
      <infinity-loading v-if="isLoading && !hasData" />
      <chart-j-s-line
        v-else-if="hasData"
        :chart-data="chartData"
        :chart-options="chartOptions"
        width="100%"
        :height="200"
      />
      <metrics-empty-state v-else />
    </div>
  </div>
</template>

<script>
import ChartJSLine from "@/components/global/chartjs/chart_js_line.vue";
import MetricsEmptyState from "../shared/metrics_empty_state.vue";
import InfinityLoading from "@/components/rubrics/newRubric/components/infinity-loading.vue";
import { Filler } from "chart.js";
import { Chart as ChartJS } from "chart.js";
import { METRICS_COLOR_STUDENT } from "@/constants";
ChartJS.register(Filler);

export default {
  name: "MetricsScoreChart",

  components: {
    ChartJSLine,
    MetricsEmptyState,
    InfinityLoading,
  },

  props: {
    data: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    hasData() {
      if (!this.data.labels?.length) return false;
      return (
        this.data.datasets?.some((ds) =>
          ds.data?.some((v) => v != null && v !== 0)
        ) ?? false
      );
    },

    chartData() {
      const raw = this.data.datasets?.[0]?.data ?? [];
      const mapped = raw.map((v) => (v == null || v === 0 ? null : v));
      const first = mapped.findIndex((v) => v != null);
      const last = mapped.findLastIndex((v) => v != null);
      const trimmed = first === -1 ? [] : mapped.slice(first, last + 1);
      const labels = (this.data.labels ?? []).slice(
        first === -1 ? 0 : first,
        first === -1 ? 0 : last + 1
      );
      const data = trimmed;
      const pointRadii = data.map((v, i) => {
        const isIsolated =
          v != null && data[i - 1] == null && data[i + 1] == null;
        return isIsolated ? 2 : 0;
      });
      return {
        labels,
        datasets: [
          {
            data,
            clip: false,
            spanGaps: true,
            borderColor: METRICS_COLOR_STUDENT,
            backgroundColor: "rgba(124, 58, 237, 0.08)",
            fill: true,
            tension: 0.4,
            pointRadius: pointRadii,
            pointBackgroundColor: METRICS_COLOR_STUDENT,
            pointHoverRadius: 4,
            borderWidth: 2,
          },
        ],
      };
    },

    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { top: 20 },
        },
        onHover: (event, activeElements) => {
          event.native.target.style.cursor =
            activeElements.length > 0 ? "pointer" : "default";
        },
        interaction: {
          mode: "index",
          intersect: false,
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.parsed.y}%`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: {
              color: "#566370",
              font: {
                family: "Inter",
                size: 12,
                weight: "400",
                lineHeight: 1.2,
              },
            },
          },
          y: {
            grid: { color: "#F0F3F6" },
            border: { display: false },
            min: 0,
            max: 100,
            ticks: {
              color: "#566370",
              font: {
                family: "Inter",
                size: 12,
                weight: "400",
                lineHeight: 1.2,
              },
              callback: (v) => `${v}%`,
              stepSize: 10,
            },
          },
        },
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../shared/metrics_chart_card";

.metrics-chart-card {
  &__body {
    min-height: 210px;
  }
}
</style>
